import { concat, hash } from '@ember/helper';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import { runTask } from 'ember-lifeline';
import { modifier } from 'ember-modifier';
import fuiDropdown from 'wise-fui-modules/modifiers/fui-dropdown';
import DropdownDivider from './dropdown/divider.gts';
import DropdownItem from './dropdown/item.gts';

interface DropdownParams {
  allowAdditions?: boolean;
  className: { addition: string };
  ignoreDiacritics?: boolean;
  forceSelection: boolean;
  fullTextSearch: boolean;
  hideAdditions: boolean;
  hideDividers: string;
  message: { addResult: string; noResults: string };
  onChange: (value: string | string[]) => void;
  onSearch?: void;
}

export interface Signature {
  Element: HTMLDivElement;
  Args: {
    selected?: string | string[];
    propertyName?: string;
    labelName?: string;
    isMandatory?: boolean;
    placeholder?: string;
    /** If true, make the dropdown clearable AND not searchable */
    clearable?: boolean;
    /** If true allow multiple selection in the dropdown */
    multipleSelection?: boolean;
    isReadOnly?: boolean;
    size?: string;
    /** If true add the possibility to add other item directly  */
    allowAdditions?: boolean;
    ignoreDiacritics?: boolean;
    noResultsText?: string;
    fullTextSearch?: boolean | 'exact';
    // Dropdown Item
    translationKey?: string;
    unfilterable?: boolean;
    // Callbacks
    onChange?: (value: string | string[]) => void;
    onSearch?: () => void;
  };
  Blocks: {
    default: [
      {
        Item: WithBoundArgs<
          typeof DropdownItem,
          'translationKey' | 'unfilterable'
        >;
        Divider: typeof DropdownDivider;
      }
    ];
  };
}

interface HTMLDropdownElement extends JQuery {
  /**
   * Type is wide because the method "dropdown" as different signature
   * Can be used:
   *  - dropdown(command: string, ...args: DropdownParams)
   *  - dropdown(command: string)
   *  - dropdown(options: DropdownParams)
   *
   * The method have also many return types according to its signature
   */
  dropdown(args: DropdownParams): void;
  dropdown(command: string, ...args: string[]): string[] | string | boolean;
}

/**
 * This component manages two types of data depending of the simple selection or
 * the multiple selection.
 *
 * - A single selection manage a string
 * - A multiple selection manage an array of strings.
 *
 * It can be used as a classic component with an
 * `onChange` callback and a `selected` attribute.
 */
export default class DropdownComponent extends Component<Signature> {
  dropdownElement: HTMLElement | null = null;
  #isUpdatingSelected = false;
  #isInitialized = false;
  _selected: Signature['Args']['selected'];

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    this._selected = args.selected;
  }

  get settings(): Record<string, unknown> {
    return {
      onChange: this.onChange.bind(this),
      onSearch: this.args.onSearch?.bind(this),

      fullTextSearch: this.args.fullTextSearch ?? true,
      ignoreDiacritics: this.args.ignoreDiacritics,
      forceSelection: false,

      hideDividers: 'empty',

      allowAdditions: this.args.allowAdditions,
      hideAdditions: false,

      className: {
        addition: 'stuck addition'
      },

      message: {
        addResult: 'Ajouter <b>{term}</b>',
        noResults: this.args.noResultsText || 'Aucun résultat.'
      }
    };
  }

  /**
   * Set the selected value of the dropdown.
   */
  _updateSelected = () => {
    this.#isUpdatingSelected = true;
    /**
     * This method will call the onChange callback.
     * So modifying the `selected` property from outside will call
     * the callback.
     * That's why we use the '#isUpdatingSelected' to prevent an inifinite loop
     */
    if (this.args.selected != null) {
      // @ts-expect-error The type of second argument of this.#execute should be Array of string
      this.#execute('set exactly', this.args.selected);
    } else {
      this.#execute('set placeholderText', this.placeholder);
    }

    this.#isUpdatingSelected = false;
  };

  updateDropdownModifier = modifier((element: HTMLDivElement) => {
    this.dropdownElement = element;

    if (!this.#isInitialized) {
      this.#isInitialized = true;
      this._updateSelected();
    } else if (this.didSelectionChange) {
      runTask(this, () => {
        this._selected = this.args.selected;
        this._updateSelected();
      });
    }
  });

  get didSelectionChange() {
    const normalize = (value: Signature['Args']['selected']): string[] => {
      if (!value) return [];
      return Array.isArray(value) ? value : [value];
    };

    const a = normalize(this._selected);
    const b = normalize(this.args.selected);

    if (a.length !== b.length) return true;

    const setA = new Set(a);

    for (const val of b) {
      if (!setA.has(val)) return true;
    }

    return false;
  }

  get placeholder() {
    return this.args.placeholder ?? 'Sélectionnez ou tapez votre texte';
  }

  onChange(/*value, text, selectedItems*/) {
    // This check avoid to call the 'onChange' callback after an
    // explicit set on the '@selected'.
    if (!this.#isUpdatingSelected) {
      this.args.onChange?.(this.#getValue());
    }
  }

  #execute = (command: string, ...args: string[]) => {
    return (
      // prettier-ignore
      // eslint-disable-next-line ember/no-jquery
      ( $(this.dropdownElement as HTMLElement) as unknown as HTMLDropdownElement
      ).dropdown(command, ...args)
    );
  };

  /**
   * Replace HTML escaped characters.
   *
   * Fomantic-UI relies on jQuery for getting content of items in the dropdown.
   * If the dropdown contains an item with an apostrophe, the way jQuery gets
   * the data (with jQuery.data) makes the content been escaped for some characters.
   *
   * This is especially true with the apostrophe which is escaped with `&#x27;`.
   *
   * This code transforms this sequence in an apostrophe.
   *
   * Example of received text: "L&#x27;acomorb". Will be transformed to "L'acomorb".
   *
   */
  #unescapeValue = (value: string): string => {
    // eslint-disable-next-line ember/no-jquery
    return $(`<div>${value}</div>`).text();
  };

  #getValue = (): string | string[] => {
    if (this.#execute('is multiple')) {
      // This step is necessary when working with a multiple dropdown because multiple values
      // is represented as a unique string.
      // We thus get the values from Fomantic UI, which will return an array if there is an item,
      // or an empty string if no value is set.
      const values = (this.#execute('get values') as string[]) || [];
      return values.map(item => this.#unescapeValue(item));
    }
    const value = this.#execute('get value') as string;
    return this.#unescapeValue(value);
  };

  <template>
    {{#if @labelName}}
      <label>
        {{@labelName}}{{if @isMandatory ' *'}}
      </label>
    {{/if}}

    <div
      class={{concat
        'ui selection'
        (if @clearable ' clearable' ' search')
        (if @multipleSelection ' multiple')
        (if @isReadOnly ' disabled semeia-readonly')
        (if @size (concat ' ' @size) ' fluid')
        ' dropdown'
      }}
      data-test-dropdown={{@propertyName}}
      ...attributes
      {{fuiDropdown this.settings}}
      {{this.updateDropdownModifier}}
    >
      <div class='default text' data-test-select-placeholder>
        {{this.placeholder}}
      </div>
      {{#unless @isReadOnly}}
        <i class='dropdown icon'></i>
      {{/unless}}

      <div class='menu'>
        {{yield
          (hash
            Item=(component
              DropdownItem
              translationKey=@translationKey
              unfilterable=@unfilterable
            )
            Divider=DropdownDivider
          )
        }}
      </div>
    </div>
  </template>
}
